import { useQuery, useMutation, useQueryClient } from 'react-query'
import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import CONSTANTS from 'utils/constants'
import type {
  ReconciliationFilters,
  ReconciliationListApiResponse,
  ReconcilePayload,
  BulkReconcilePayload,
} from '../types/reconciliation'

export const RECONCILIATION_QUERY_KEY = 'reconciliation'

function decodeQueryStringForLog(qs: string): string {
  if (!qs) return ''
  return qs
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=')
      if (eq === -1) return decodeURIComponent(pair)
      return `${decodeURIComponent(pair.slice(0, eq))}=${decodeURIComponent(pair.slice(eq + 1))}`
    })
    .join('&')
}

function logReconciliationGetRequestUrl(qs: string) {
  const urlPath = `reconciliation?${qs}`
  const baseUrl = CONSTANTS.VITE_APP_ROTA_API
  const fullUrl = baseUrl
    ? `${baseUrl}${urlPath.startsWith('/') ? '' : '/'}${urlPath}`
    : urlPath
  const q = fullUrl.indexOf('?')
  const unencoded =
    q === -1
      ? fullUrl
      : `${fullUrl.slice(0, q + 1)}${decodeQueryStringForLog(fullUrl.slice(q + 1))}`
  console.log('[reconciliation GET]', unencoded)
}

export const buildReconciliationQueryString = (
  filters: ReconciliationFilters
): string => {
  const params = new URLSearchParams()
  params.set('startTime', filters.startTime)
  params.set('endTime', filters.endTime)
  filters.status?.forEach((s) => params.append('status', s))
  filters.teamMemberIds?.forEach((id) => params.append('teamMemberIds', id))
  filters.serviceUserIds?.forEach((id) => params.append('serviceUserIds', id))
  filters.eventTypeIds?.forEach((id) => params.append('eventTypeIds', id))
  filters.durationDiscrepancy?.forEach((d) =>
    params.append('durationDiscrepancy', d)
  )
  if (filters.page !== undefined && filters.pageSize !== undefined) {
    params.set('pagination[page]', String(Math.max(1, filters.page)))
    params.set('pagination[limit]', String(filters.pageSize))
  }
  if (filters.sortField !== undefined && filters.sortDir !== undefined) {
    params.set(`sort[${filters.sortField}]`, String(filters.sortDir))
  } else {
    params.set('sort[startTime]', '-1')
  }
  return params.toString()
}

export const useGetReconciliation = (filters: ReconciliationFilters) => {
  const qs = buildReconciliationQueryString(filters)
  return useQuery<ReconciliationListApiResponse>(
    [RECONCILIATION_QUERY_KEY, filters],
    () => {
      logReconciliationGetRequestUrl(qs)
      return getJSON({
        url: `reconciliation?${qs}`,
        isRota: true,
      })
    },
    {
      staleTime: 60 * 1000,
      keepPreviousData: true,
    }
  )
}

export const useReconcileOne = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      rotaInstanceId,
      teamMemberId,
      payload,
    }: {
      rotaInstanceId: string
      teamMemberId: string
      payload: ReconcilePayload
    }) =>
      postJSON({
        url: `reconciliation/${rotaInstanceId}/${teamMemberId}`,
        method: 'PATCH',
        isRota: true,
        json: payload,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY]),
      onError: () => showToast({ type: 'error', title: 'Failed to reconcile. Please try again.' }),
    }
  )
}

export const useBulkReconcile = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (payload: BulkReconcilePayload) =>
      postJSON({
        url: 'reconciliation/bulk',
        isRota: true,
        json: payload,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY]),
      onError: () => showToast({ type: 'error', title: 'Failed to reconcile. Please try again.' }),
    }
  )
}

export const useExportReconciliation = () =>
  useMutation(
    async (filters: ReconciliationFilters) => {
      const qs = buildReconciliationQueryString(filters)
      const result = await getJSON({
        url: `reconciliation/export?${qs}`,
        isRota: true,
        responseType: 'arrayBuffer',
      })
      const blob = new Blob([result as ArrayBuffer], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'reconciliation.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    {
      onError: () => showToast({ type: 'error', title: 'Failed to download CSV. Please try again.' }),
    }
  )
